// Controlador para Catálogo de Unidades de Medida
// Siguiendo MODO1: cat_umedida_controller
import axios from 'axios';
import { getApiUrl } from '@/config/api';
import authService from '@/shared/services/auth';

export default class CatUMedidaService {
  constructor() {
    this.unidadesMedida = [];
    this.loading = false;
    this.error = null;
    this.selectedEmpresa = null;
  }

  // Inicializar el servicio
  async initialize() {
    // Esperar un poco para que la empresa se seleccione
    await this.waitForEmpresa();
    await this.listar();
  }

  // Esperar a que la empresa esté seleccionada
  async waitForEmpresa() {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo
    
    while (attempts < maxAttempts) {
      this.selectedEmpresa = authService.getSelectedEmpresa();
      if (this.selectedEmpresa && this.selectedEmpresa.EmpresaId) {
        console.log('Empresa encontrada para CatUMedida:', this.selectedEmpresa);
        return;
      }
      
      // Esperar 100ms antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    // Si no se encuentra empresa después de los intentos
    this.error = 'No se pudo obtener la empresa seleccionada';
    console.error('No se pudo obtener empresa para CatUMedida después de', maxAttempts, 'intentos');
  }

  // Listar unidades de medida
  async listar() {
    this.loading = true;
    this.error = null;
    
    try {
      // Verificar que tenemos empresa seleccionada
      if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
        this.selectedEmpresa = authService.getSelectedEmpresa();
        if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
          this.loading = false;
          return { success: false, data: [], message: 'Debe elegir una empresa antes de poder continuar.' };
        }
      }

      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      console.log('Cargando unidades de medida para empresa:', this.selectedEmpresa.EmpresaId);

      const response = await axios.post(getApiUrl('/api/CatUMedida/listar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      
      // Filtrar por empresa en el frontend como medida de seguridad adicional
      const allUnidadesMedida = response.data || [];
      this.unidadesMedida = allUnidadesMedida.filter(unidadesMedid => unidadesMedid.EmpresaId === this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('UnidadesMedida cargados:', this.unidadesMedida.length, 'de', allUnidadesMedida.length, 'total');
      console.log('Empresa del usuario:', this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('Unidades de medida cargadas:', this.unidadesMedida.length);
      
      return {
        success: true,
        data: this.unidadesMedida,
        message: 'Unidades de medida cargadas exitosamente'
      };
    } catch (error) {
      console.error('Error al cargar unidades de medida:', error);
      this.error = error.message;
      this.unidadesMedida = [];
      this.loading = false;
      throw error;
    }
  }

  // Guardar unidad de medida (insert/update)
  async guardar(unidadMedida) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      // Asegurar que tenga la empresa seleccionada
      unidadMedida.EmpresaId = this.selectedEmpresa.EmpresaId;

      const response = await axios.post(getApiUrl('/api/CatUMedida/guardar'), unidadMedida, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Recargar la lista después de guardar
      await this.listar();
      
      return {
        success: true,
        data: response.data,
        message: unidadMedida.Clave ? 'Unidad de medida actualizada exitosamente' : 'Unidad de medida creada exitosamente'
      };
    } catch (error) {
      console.error('Error al guardar unidad de medida:', error);
      throw error;
    }
  }

  // Borrar unidad de medida
  async borrar(unidadMedida) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatUMedida/borrar'), unidadMedida, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Recargar la lista después de eliminar
      await this.listar();
      
      return {
        success: true,
        data: response.data,
        message: 'Unidad de medida eliminada exitosamente'
      };
    } catch (error) {
      console.error('Error al eliminar unidad de medida:', error);
      throw error;
    }
  }

  // Leer una unidad de medida específica
  async lectura(clave) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatUMedida/lectura'), {
        Clave: clave,
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return {
        success: true,
        data: response.data,
        message: 'Unidad de medida encontrada'
      };
    } catch (error) {
      console.error('Error al leer unidad de medida:', error);
      throw error;
    }
  }

  // Recargar unidades de medida
  async recargar() {
    await this.listar();
  }

  // Descarga masiva
  async downloadBulk() {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatUMedida/descargar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Crear blob y descargar
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `unidades_medida_${this.selectedEmpresa.Nombre}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error al descargar unidades de medida:', error);
      throw error;
    }
  }

  // Método para obtener configuración de columnas
  getDataGridColumns() {
    return [
      { key: 'Clave', label: 'Clave', sortable: true, required: true },
      { key: 'Nombre', label: 'Nombre', sortable: true, required: true },
      { key: 'Clave2', label: 'Clave 2', sortable: true },
      { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
      { 
        key: 'EsActivo', 
        label: 'Activo', 
        sortable: true,
        format: (value) => value ? 'Sí' : 'No'
      }
    ];
  }

  // Método para obtener campos del formulario
  getFormFields() {
    return [
      {
        name: 'Clave',
        label: 'Clave',
        type: 'text',
        required: true,
        maxlength: 50
      },
      {
        name: 'Nombre',
        label: 'Nombre',
        type: 'text',
        required: true,
        maxlength: 100
      },
      {
        name: 'Clave2',
        label: 'Clave 2',
        type: 'text',
        maxlength: 50
      },
      {
        name: 'ClaveErp',
        label: 'Clave ERP',
        type: 'text',
        maxlength: 50
      },
      {
        name: 'EsActivo',
        label: 'Activo',
        type: 'checkbox',
        default: true
      }
    ];
  }
}
